/**
 * AccountPicker — the A2.3 company field: a type-ahead over GET
 * /v1/sales/accounts with an inline "create" row.
 *
 * Contract: the input's text IS the deal's company (free text stays legal —
 * P1-2 kept company as the fallback for unlinked deals), and picking a row
 * also sets accountId. Any edit after a pick clears accountId: the link is
 * to a NAMED account, and a changed name is no longer that account.
 *
 * The create row goes through POST /v1/sales/accounts; when the name is
 * already taken (the 0116 per-tenant unique → 409 with existingAccountId)
 * the picker links the existing account instead of surfacing a dead end.
 *
 * Visual (docs/design/stier/ADOPTION.md): the input keeps the control
 * radius (§8.4 REFUSED list — text inputs never capsule); the results panel
 * is a .k-card (§3.4 contract) under the input; rows are plain hover rows;
 * the create row is the quiet action (§5 EmptyState precedent: .k-btn-quiet,
 * never a filled button inside a list). No new tokens.
 *
 * §25 L-1: the CONTACT surfaces mount the same picker with `allowCreate`
 * false — an explicit pick only, no create row (P1-1: no accounts minted
 * from contacts), so a company nobody has a deal with shows "No matching
 * account" and nothing to pick — and `clearable`, which turns the linked
 * mark into an "Unlink account" control (L-2: a link can be cleared). The
 * deal surfaces pass neither and render exactly as before.
 */

import { useEffect, useRef, useState } from 'react'
import { Building2, Plus, Search, X } from 'lucide-react'
import { fetchAccounts, createAccount, conflictingAccountId } from '../../api'
import type { SalesAccountRow } from '../../types'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'

export interface AccountPick {
  /** '' when the company text is free text (no linked account). */
  accountId: string
  company: string
}

export function AccountPicker({ value, onChange, className, placeholder, autoFocus, id, allowCreate = true, clearable = false, onBlur }: {
  value: AccountPick
  onChange: (next: AccountPick) => void
  className: string
  placeholder?: string
  autoFocus?: boolean
  id?: string
  /** false = pick only: no create row, ever (the contact surfaces, P1-1). */
  allowCreate?: boolean
  /** true = a linked pick shows an "Unlink account" control that clears the
   *  id and keeps the text (the contact surfaces, L-2). */
  clearable?: boolean
  /** Fires when the input loses focus — the L-4 suggestion hook's trigger. */
  onBlur?: () => void
}) {
  const [open, setOpen] = useState(false)
  const [rows, setRows] = useState<SalesAccountRow[]>([])
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  const q = value.company

  // Debounced server search while the panel is open (the server matches on
  // name_normalised, so case and edge whitespace never matter).
  useEffect(() => {
    if (!open) return
    let alive = true
    const t = setTimeout(() => {
      setLoading(true)
      fetchAccounts(q, 8)
        .then(r => { if (alive) setRows(r) })
        .catch(() => { if (alive) setRows([]) })
        .finally(() => { if (alive) setLoading(false) })
    }, 200)
    return () => { alive = false; clearTimeout(t) }
  }, [open, q])

  // Click-away closes the panel.
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const trimmed = q.trim()
  const exact = rows.find(r => r.name.toLowerCase() === trimmed.toLowerCase())

  function pick(r: { id: string; name: string }) {
    onChange({ accountId: r.id, company: r.name })
    setOpen(false)
  }

  async function create() {
    if (!trimmed || creating) return
    setCreating(true)
    try {
      const a = await createAccount({ name: trimmed })
      toast.success(`Account "${a.name}" created`)
      pick(a)
    } catch (e) {
      const existing = conflictingAccountId(e)
      if (existing) { pick({ id: existing, name: trimmed }); return }
      toast.error(apiErrorMessage(e, 'Failed to create account'))
    } finally {
      setCreating(false)
    }
  }

  return (
    <div ref={wrap} className="relative">
      <input id={id} type="text" value={q} autoFocus={autoFocus} autoComplete="off"
        role="combobox" aria-expanded={open} aria-autocomplete="list" aria-controls={id ? `${id}-listbox` : undefined}
        onChange={e => { onChange({ accountId: '', company: e.target.value }); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onBlur={onBlur}
        onKeyDown={e => { if (e.key === 'Escape') setOpen(false) }}
        placeholder={placeholder ?? 'Search accounts…'} className={className} />
      {/* §25 (the L-9 recon's finding): at rest an UNLINKED picker holding
          text was indistinguishable from the old free-text input — nothing
          said it could pick. The contact surfaces (clearable) now carry a
          faint search mark whenever text is present and no link is; an empty
          field carries nothing, so the New Contact drawer's pin holds. */}
      {clearable && !value.accountId && trimmed !== '' && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" title="Not linked to an account — type to search, or pick a suggestion">
          <Search size={13} />
        </span>
      )}
      {value.accountId && (clearable ? (
        <button type="button" onClick={() => onChange({ accountId: '', company: value.company })}
          aria-label="Unlink account" title="Unlink account"
          className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-ink-faint hover:text-ink-primary transition-colors">
          <Building2 size={13} /><X size={11} />
        </button>
      ) : (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" title="Linked account">
          <Building2 size={13} />
        </span>
      ))}
      {open && (
        <div id={id ? `${id}-listbox` : undefined} role="listbox"
          className="k-card absolute left-0 right-0 z-20 mt-1 overflow-hidden">
          {loading && rows.length === 0 && (
            <div className="px-4 py-3 text-12 text-ink-muted">Searching…</div>
          )}
          {rows.map(r => (
            <button key={r.id} type="button" role="option" aria-selected={r.id === value.accountId}
              onClick={() => pick(r)}
              className="w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-surface-row-hover transition-colors">
              <span className="text-13 font-medium text-ink-primary truncate">{r.name}</span>
              <span className="text-11 text-ink-faint whitespace-nowrap">
                {r.openDealCount} open · {r.contactCount} contact{r.contactCount === 1 ? '' : 's'}
              </span>
            </button>
          ))}
          {!loading && rows.length === 0 && (
            <div className="px-4 py-3 text-12 text-ink-muted">
              {trimmed === '' ? 'Type to search accounts' : 'No matching account'}
            </div>
          )}
          {allowCreate && trimmed !== '' && !exact && (
            <button type="button" onClick={create} disabled={creating}
              className="k-btn-quiet w-full flex items-center gap-2 px-4 py-2.5 border-t border-edge-row rounded-none disabled:opacity-50">
              <Plus size={12} /> {creating ? 'Creating…' : `Create account "${trimmed}"`}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
